import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatNumber } from '@/lib/utils';

import type { CalibracionStats } from '../calibracion.types';

interface Celda {
  label: string;
  valor: string;
  tono?: string;
}

/**
 * Indicadores de los códigos de calibración.
 *
 * Los conteos usan el **estado efectivo** que calcula el backend (un código
 * activo puede haber caducado o quedarse sin usos), así que suman igual que el
 * listado filtrado.
 */
export function CalibracionStatsPanel({
  stats,
  loading,
}: {
  stats: CalibracionStats | undefined;
  loading: boolean;
}) {
  const celdas: Celda[] = stats
    ? [
        { label: 'Total de códigos', valor: formatNumber(stats.total_codigos) },
        { label: 'Activos', valor: formatNumber(stats.codigos_activos), tono: 'text-emerald-600' },
        { label: 'Expirados', valor: formatNumber(stats.codigos_expirados), tono: 'text-amber-600' },
        { label: 'Consumidos', valor: formatNumber(stats.codigos_consumidos), tono: 'text-brand-600' },
        { label: 'Inactivos', valor: formatNumber(stats.codigos_inactivos) },
        {
          label: 'Usos restantes',
          valor: `${formatNumber(stats.total_usos_restantes)} / ${formatNumber(stats.total_usos_disponibles)}`,
        },
      ]
    : [];

  if (loading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} className="h-16 rounded-card" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {celdas.map((celda) => (
        <div key={celda.label} className="rounded-card border border-ink-100 bg-white px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
            {celda.label}
          </p>
          <p
            className={cn(
              'mt-1 font-mono text-lg font-bold tabular-nums text-ink-900',
              celda.tono,
            )}
          >
            {celda.valor}
          </p>
        </div>
      ))}

      {stats && stats.total_usos_registrados > 0 ? (
        <div className="sm:col-span-2 xl:col-span-3">
          <Badge variant="outline">
            {formatNumber(stats.total_usos_registrados)}{' '}
            {stats.total_usos_registrados === 1 ? 'uso registrado' : 'usos registrados'}
          </Badge>
        </div>
      ) : null}
    </div>
  );
}
